import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { uploadMediaFile } from '@/hooks/useUpload';
import {
  LAYOUT_OPTIONS,
  emptyExtraBlock,
  loadThemeExtraBlocks,
  normalizeExtraBlock,
  saveThemeExtraBlocks,
  type ThemeExtraBlock,
} from '@/lib/themeExtraBlocks';

// ─────────────────────────────────────────────────────────────
// « Contenu du thème » — blocs supplémentaires propres à un thème.
// Chaque bloc : titre, description, bouton, image, image de fond.
// Tous les champs sont facultatifs et peuvent être masqués un par un.
// ─────────────────────────────────────────────────────────────

interface FieldToggleProps {
  label: string;
  active: boolean;
  onToggle: () => void;
}

function FieldToggle({ label, active, onToggle }: FieldToggleProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap cursor-pointer border border-background-200/70 bg-background-50 text-foreground-500 hover:bg-background-100 transition-colors"
      title={active ? `Masquer ${label}` : `Afficher ${label}`}
    >
      <i className={active ? 'ri-eye-line text-accent-600' : 'ri-eye-off-line'}></i>
      {label}
    </button>
  );
}

function BlockEditor({
  block,
  index,
  total,
  onChange,
  onDelete,
  onMove,
}: {
  block: ThemeExtraBlock;
  index: number;
  total: number;
  onChange: (patch: Partial<ThemeExtraBlock>) => void;
  onDelete: () => void;
  onMove: (direction: -1 | 1) => void;
}) {
  const [uploading, setUploading] = useState<'image' | 'backgroundImage' | null>(null);
  const [uploadError, setUploadError] = useState('');

  const handleUpload = async (field: 'image' | 'backgroundImage', file?: File) => {
    if (!file) return;
    setUploading(field);
    setUploadError('');
    try {
      const url = await uploadMediaFile(file, 'theme-blocks');
      onChange({ [field]: url } as Partial<ThemeExtraBlock>);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "L'image n'a pas pu être envoyée.");
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className={`bg-background-50 border rounded-xl overflow-hidden ${block.hidden ? 'border-background-200/50 opacity-70' : 'border-background-200/70'}`}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-background-200/60 bg-background-100/40">
        <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
          {index + 1}
        </span>
        <p className="text-xs font-semibold text-foreground-800 truncate flex-1">
          {block.title.trim() || 'Bloc sans titre'}
        </p>
        <button
          type="button"
          onClick={() => onMove(-1)}
          disabled={index === 0}
          className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200/70 disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed"
          aria-label="Monter"
        >
          <i className="ri-arrow-up-line text-sm"></i>
        </button>
        <button
          type="button"
          onClick={() => onMove(1)}
          disabled={index === total - 1}
          className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200/70 disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed"
          aria-label="Descendre"
        >
          <i className="ri-arrow-down-line text-sm"></i>
        </button>
        <button
          type="button"
          onClick={() => onChange({ hidden: !block.hidden })}
          className={`w-7 h-7 flex items-center justify-center rounded-md cursor-pointer ${block.hidden ? 'text-foreground-400 hover:bg-background-200/70' : 'text-accent-600 hover:bg-accent-50'}`}
          aria-label="Visibilité du bloc"
          title={block.hidden ? 'Bloc masqué' : 'Bloc visible'}
        >
          <i className={block.hidden ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'}></i>
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 cursor-pointer"
          aria-label="Supprimer"
        >
          <i className="ri-delete-bin-line text-sm"></i>
        </button>
      </div>

      <div className="p-4 space-y-4">
        {block.hidden && (
          <p className="text-[11px] text-foreground-500 flex items-center gap-1.5">
            <i className="ri-eye-off-line"></i>
            Ce bloc est masqué sur le site public.
          </p>
        )}

        <div>
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <label className="text-[11px] font-semibold text-foreground-600">Titre</label>
            <FieldToggle label="Titre" active={!block.hideTitle} onToggle={() => onChange({ hideTitle: !block.hideTitle })} />
          </div>
          <input
            type="text"
            value={block.title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder="Ex : Notre atelier, La maison, etc."
            maxLength={120}
            disabled={block.hideTitle}
            className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 disabled:opacity-50"
          />
        </div>

        <div>
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <label className="text-[11px] font-semibold text-foreground-600">Description</label>
            <FieldToggle label="Texte" active={!block.hideDescription} onToggle={() => onChange({ hideDescription: !block.hideDescription })} />
          </div>
          <textarea
            value={block.description}
            onChange={(e) => onChange({ description: e.target.value })}
            rows={3}
            maxLength={500}
            placeholder="Textes libres, programme, informations…"
            disabled={block.hideDescription}
            className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 resize-none disabled:opacity-50"
          />
          <p className="text-[10px] text-foreground-400 mt-1">{block.description.length}/500</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label className="text-[11px] font-semibold text-foreground-600">Bouton</label>
              <FieldToggle label="Bouton" active={!block.hideButton} onToggle={() => onChange({ hideButton: !block.hideButton })} />
            </div>
            <input
              type="text"
              value={block.buttonLabel}
              onChange={(e) => onChange({ buttonLabel: e.target.value })}
              placeholder="Libellé (ex : Nous contacter)"
              maxLength={40}
              disabled={block.hideButton}
              className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-foreground-600 mb-1.5">Lien du bouton</label>
            <input
              type="text"
              value={block.buttonHref}
              onChange={(e) => onChange({ buttonHref: e.target.value })}
              placeholder="/contact ou https://…"
              disabled={block.hideButton}
              className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 disabled:opacity-50"
            />
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-foreground-600 mb-1.5">Disposition</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {LAYOUT_OPTIONS.map((layout) => (
              <button
                key={layout.value}
                type="button"
                onClick={() => onChange({ layout: layout.value })}
                className={`flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg border text-[10px] font-semibold cursor-pointer transition-colors ${
                  block.layout === layout.value
                    ? 'border-primary-300 bg-primary-50 text-primary-700'
                    : 'border-background-200/70 bg-background-50 text-foreground-500 hover:bg-background-100'
                }`}
              >
                <i className={`${layout.icon} text-sm`}></i>
                {layout.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label className="text-[11px] font-semibold text-foreground-600">Image</label>
              <FieldToggle label="Image" active={!block.hideImage} onToggle={() => onChange({ hideImage: !block.hideImage })} />
            </div>
            {block.image ? (
              <div className="flex items-center gap-2">
                <div className="w-16 h-12 rounded-md overflow-hidden bg-background-100 flex-shrink-0">
                  <img src={block.image} alt="" className="w-full h-full object-cover object-top" />
                </div>
                <button
                  type="button"
                  onClick={() => onChange({ image: '' })}
                  className="px-2.5 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-[10px] font-semibold text-foreground-600 hover:bg-background-200/70 cursor-pointer whitespace-nowrap"
                >
                  Retirer
                </button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-1.5 h-12 rounded-lg border border-dashed border-background-300 bg-background-50 text-[11px] font-medium text-foreground-600 hover:bg-background-100 cursor-pointer">
                {uploading === 'image' ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-image-add-line"></i>}
                {uploading === 'image' ? 'Envoi…' : 'Choisir une image'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => void handleUpload('image', e.target.files?.[0])} />
              </label>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-foreground-600 mb-1.5">Image d&apos;arrière-plan</label>
            {block.backgroundImage ? (
              <div className="flex items-center gap-2">
                <div className="w-16 h-12 rounded-md overflow-hidden bg-background-100 flex-shrink-0">
                  <img src={block.backgroundImage} alt="" className="w-full h-full object-cover object-top" />
                </div>
                <button
                  type="button"
                  onClick={() => onChange({ backgroundImage: '' })}
                  className="px-2.5 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-[10px] font-semibold text-foreground-600 hover:bg-background-200/70 cursor-pointer whitespace-nowrap"
                >
                  Retirer
                </button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-1.5 h-12 rounded-lg border border-dashed border-background-300 bg-background-50 text-[11px] font-medium text-foreground-600 hover:bg-background-100 cursor-pointer">
                {uploading === 'backgroundImage' ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-image-line"></i>}
                {uploading === 'backgroundImage' ? 'Envoi…' : 'Choisir un fond'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => void handleUpload('backgroundImage', e.target.files?.[0])} />
              </label>
            )}
          </div>
        </div>

        {uploadError && (
          <p className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{uploadError}</p>
        )}

        {/* Aperçu en direct du bloc en cours d'édition */}
        <div className="pt-3 border-t border-background-200/60">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-foreground-400 mb-2 flex items-center gap-1.5">
            <i className="ri-eye-line"></i>Aperçu en direct
          </p>
          <div className="relative overflow-hidden rounded-xl border border-background-200/70 bg-background-50">
            {block.backgroundImage ? (
              <>
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${block.backgroundImage})` }} aria-hidden="true"></div>
                <div className="absolute inset-0 bg-background-50/85" aria-hidden="true"></div>
              </>
            ) : null}
            <div className="relative p-4">
              <div className={`flex ${block.layout === 'center' || block.layout === 'full' ? 'flex-col items-center text-center gap-3' : block.layout === 'image-right' ? 'flex-row-reverse items-center gap-3' : 'flex-row items-center gap-3'}`}>
                {!block.hideImage && block.image && (
                  <div className="w-24 h-20 rounded-lg overflow-hidden bg-background-100 flex-shrink-0">
                    <img src={block.image} alt="" className="w-full h-full object-cover object-top" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  {!block.hideTitle && block.title.trim() && (
                    <h4 className="text-sm font-bold font-heading text-foreground-950 leading-snug">{block.title}</h4>
                  )}
                  {!block.hideDescription && block.description.trim() && (
                    <p className="text-[11px] text-foreground-600 leading-relaxed mt-1 whitespace-pre-line line-clamp-4">{block.description}</p>
                  )}
                  {!block.hideButton && block.buttonLabel.trim() && (
                    <span className="inline-flex items-center gap-1 mt-2 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-[10px] font-semibold whitespace-nowrap">
                      {block.buttonLabel}<i className="ri-arrow-right-line"></i>
                    </span>
                  )}
                  {block.hideTitle && block.hideDescription && block.hideButton && (!block.image || block.hideImage) && (
                    <p className="text-[11px] text-foreground-400 italic">Rien à afficher pour l&apos;instant.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          {block.hidden && (
            <p className="text-[10px] text-foreground-400 mt-1.5 flex items-center gap-1">
              <i className="ri-eye-off-line"></i>Bloc masqué : il n&apos;apparaîtra pas sur le site.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ThemeBlocksPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const queryThemeId = parseInt(searchParams.get('theme') || '', 10);
  const commerceId = user?.id || 0;

  const [idtheme, setIdtheme] = useState<number>(Number.isNaN(queryThemeId) ? 0 : queryThemeId);
  const [themeName, setThemeName] = useState('');
  const [blocks, setBlocks] = useState<ThemeExtraBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const resolveTheme = useCallback(async () => {
    if (!commerceId) return;
    try {
      const { data } = await supabase
        .from('sitewebthemeactuelle')
        .select('idtheme, nomtheme, status')
        .eq('idcommerce', commerceId)
        .maybeSingle();
      if (!Number.isNaN(queryThemeId)) {
        setIdtheme(queryThemeId);
        setThemeName(data?.nomtheme || '');
        return;
      }
      if (data?.idtheme) setIdtheme(Number(data.idtheme));
      setThemeName(data?.nomtheme || '');
    } catch {
      /* silencieux */
    }
  }, [commerceId, queryThemeId]);

  const load = useCallback(async () => {
    if (!commerceId) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const rows = await loadThemeExtraBlocks(Number(commerceId));
      setBlocks(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger les blocs du thème.');
    } finally {
      setLoading(false);
    }
  }, [commerceId]);

  useEffect(() => { void resolveTheme(); }, [resolveTheme]);
  useEffect(() => { void load(); }, [load]);

  const totalVisible = useMemo(() => blocks.filter((b) => !b.hidden).length, [blocks]);

  const patchBlock = (id: string, patch: Partial<ThemeExtraBlock>) => {
    setBlocks((prev) => prev.map((block) => (block.id === id ? { ...block, ...patch } : block)));
  };

  const moveBlock = (index: number, direction: -1 | 1) => {
    setBlocks((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next;
    });
  };

  const removeBlock = (id: string) => {
    setBlocks((prev) => prev.filter((block) => block.id !== id));
  };

  const addBlock = () => {
    setBlocks((prev) => [...prev, emptyExtraBlock(prev.length)]);
  };

  const save = async () => {
    if (!commerceId) return;
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await saveThemeExtraBlocks(Number(commerceId), idtheme || 0, blocks.map((b, i) => normalizeExtraBlock(b, i)));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Les blocs n'ont pas pu être enregistrés.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-layout-masonry-line text-primary-500"></i>
            Contenu du thème
          </h1>
          <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
            Blocs supplémentaires de votre thème {themeName ? <strong className="text-foreground-700">« {themeName} »</strong> : null} :
            titre, description, bouton, image et image de fond. Tout est facultatif et chaque champ peut être masqué.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/dashboard/themestore"
            className="flex items-center gap-2 px-4 h-10 rounded-full bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-700 whitespace-nowrap hover:bg-background-200/70 transition-colors no-underline"
          >
            <i className="ri-arrow-left-line"></i>
            ThemeStore
          </Link>
          <button
            type="button"
            onClick={addBlock}
            className="flex items-center gap-2 px-4 h-10 rounded-full bg-primary-50 text-primary-700 text-sm font-semibold whitespace-nowrap hover:bg-primary-100 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>
            Ajouter un bloc
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving || loading}
            className="flex items-center gap-2 px-5 h-10 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            Enregistrer
          </button>
        </div>
      </div>

      {saved && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-accent-50 border border-accent-200/70 text-xs text-accent-800 flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i>
          Blocs enregistrés — ils sont déjà visibles sur votre page d&apos;accueil publique.
        </div>
      )}
      {error && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-4">
            {blocks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-14 bg-background-50 border border-background-200/70 rounded-xl text-center px-6">
                <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-layout-masonry-line text-xl text-foreground-500"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800">Aucun bloc pour ce thème</h3>
                <p className="text-xs text-foreground-500 mt-1 max-w-sm mb-4">
                  Ajoutez un bloc « en plus » : il apparaîtra sur la page d&apos;accueil de votre site public.
                </p>
                <button
                  type="button"
                  onClick={addBlock}
                  className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  Créer mon premier bloc
                </button>
              </div>
            ) : (
              blocks.map((block, index) => (
                <BlockEditor
                  key={block.id}
                  block={block}
                  index={index}
                  total={blocks.length}
                  onChange={(patch) => patchBlock(block.id, patch)}
                  onDelete={() => removeBlock(block.id)}
                  onMove={(direction) => moveBlock(index, direction)}
                />
              ))
            )}
          </div>

          <div className="lg:sticky lg:top-6 h-fit bg-background-50 border border-background-200/70 rounded-xl p-5">
            <h3 className="text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
              <i className="ri-information-line text-primary-500"></i>
              Comment ça marche
            </h3>
            <ul className="space-y-2.5 text-[11px] text-foreground-600">
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-blank-circle-fill text-[4px] mt-1.5 text-accent-500"></i>
                <span>Les blocs s&apos;affichent sur la page d&apos;accueil de votre site public, dans l&apos;ordre ci-dessus.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-blank-circle-fill text-[4px] mt-1.5 text-accent-500"></i>
                <span>Un champ laissé vide (ou masqué) n&apos;apparaît jamais sur le site.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-blank-circle-fill text-[4px] mt-1.5 text-accent-500"></i>
                <span>L&apos;œil du bloc le masque entièrement, sans le supprimer.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-blank-circle-fill text-[4px] mt-1.5 text-accent-500"></i>
                <span>Les images sont affichées en cadre fixe et responsives (2 colonnes sur mobile).</span>
              </li>
            </ul>
            <div className="mt-4 pt-4 border-t border-background-200/60 flex items-center justify-between text-[11px]">
              <span className="text-foreground-500">Blocs visibles</span>
              <span className="font-bold text-foreground-900">{totalVisible} / {blocks.length}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}